import { Folder, LayoutGrid, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { BrowsableItem } from './types';

interface FolderPickerListItemProps {
  item: BrowsableItem;
  selected: boolean;
  focused: boolean;
  unavailable: boolean;
  isCurrent: boolean;
  onSelect: (item: BrowsableItem) => void;
  onNavigate: (id: string) => void;
}

function hasChildren(item: BrowsableItem): boolean {
  const r = item.resources;
  return !!r && ((r.folder ?? 0) > 0 || (r.project ?? 0) > 0);
}

export function FolderPickerListItem({
  item,
  selected,
  focused,
  unavailable,
  isCurrent,
  onSelect,
  onNavigate,
}: FolderPickerListItemProps) {
  const drillable = hasChildren(item);

  const handleClick = () => {
    if (unavailable) return;
    onSelect(item);
  };

  const handleDoubleClick = () => {
    if (unavailable || !drillable) return;
    onNavigate(item.id);
  };

  const handleDrillClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!unavailable) onNavigate(item.id);
  };

  const Icon = item.itemtype === 'project' ? LayoutGrid : Folder;

  return (
    <div
      role="option"
      aria-selected={selected}
      tabIndex={-1}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors select-none',
        unavailable && 'cursor-not-allowed opacity-50',
        !unavailable && !selected && 'cursor-pointer hover:bg-secondary',
        selected && 'border border-primary bg-primary-muted',
        !selected && 'border border-transparent',
        focused && !selected && 'ring-2 ring-ring ring-offset-1',
      )}
    >
      <Icon
        className={cn(
          'h-5 w-5 shrink-0',
          selected ? 'text-primary' : 'text-muted-foreground',
        )}
      />

      <span className={cn('min-w-0 flex-1 truncate text-sm', selected ? 'font-medium text-foreground' : 'text-accent-foreground')}>
        {item.title}
      </span>

      {isCurrent && (
        <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
          Current
        </span>
      )}

      {drillable && !unavailable && (
        <button
          type="button"
          onClick={handleDrillClick}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-muted-foreground"
          title={`Open ${item.title}`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
